import type { IShowCatalog, IShowCatalogProduct } from '@pkg/lib-data'

import { getDomainWebsite } from './ssr'

/**
 * Fetches shows catalog from the dynamic JSON file (client-side)
 * This is called every time the widget renders, not at build time
 */
export const fetchShowsCatalogClient = async (): Promise<IShowCatalog | null> => {
  try {
    const domain = getDomainWebsite()
    const catalogUrl = `${domain}/dynamic/client/shows.json`

    const response = await fetch(catalogUrl, {
      // Add cache headers for better performance
      headers: {
        'Cache-Control': 'max-age=300', // Cache for 5 minutes
      },
    })

    if (!response.ok) {
      console.error(`[Cards List Widget] Failed to fetch shows catalog: ${response.status} ${response.statusText}`)

      return null
    }

    const catalog: IShowCatalog = await response.json()

    return catalog
  } catch (error) {
    console.error('[Cards List Widget] Error fetching shows catalog:', error)

    return null
  }
}

/**
 * MV `areaName` in shows catalog per SEO region slug.
 * Kept in sync with apps/seo/ui/src/lib/show-seo-regions.ts and filterShowCatalogByRegionSlug.
 */
const SHOW_MV_AREA_CENTRAL = 'תל אביב והמרכז'
const SHOW_MV_AREA_SHARON = 'השרון'

const SHOW_SEO_REGION_PRIMARY_AREA: Record<string, string> = {
  central: SHOW_MV_AREA_CENTRAL,
  south: 'באר שבע והדרום',
  sharon: SHOW_MV_AREA_SHARON,
  jerusalem: 'ירושלים',
  shfela: 'איזור השפלה',
  north: 'חיפה והצפון',
}

const filterByPrimaryArea = (products: IShowCatalogProduct[], areaName: string): IShowCatalogProduct[] => {
  return products.filter((product) => {
    return product.areaName === areaName
  })
}

/**
 * Filters catalog products by SEO region slug (same rules as apps/seo/ui/src/lib/fetch-events.ts).
 * Central: primary area, then Sharon fallback; other regions: single primary area.
 */
export const filterShowCatalogByRegionSlug = (
  products: IShowCatalogProduct[],
  region: string,
): IShowCatalogProduct[] => {
  if (region === 'central') {
    const centralEvents = filterByPrimaryArea(products, SHOW_MV_AREA_CENTRAL)

    if (centralEvents.length > 0) {
      return centralEvents
    }

    return filterByPrimaryArea(products, SHOW_MV_AREA_SHARON)
  }

  const primary = SHOW_SEO_REGION_PRIMARY_AREA[region]

  if (!primary) {
    return []
  }

  return filterByPrimaryArea(products, primary)
}

/**
 * Filters events by central region
 * Central region means areaName === 'תל אביב והמרכז'
 * Falls back to 'השרון' if no central events found
 */
export const filterEventsByCentralRegion = (products: IShowCatalogProduct[]): IShowCatalogProduct[] => {
  return filterShowCatalogByRegionSlug(products, 'central')
}

/**
 * Fetches shows catalog products for the widget: when `region` is set (data-region on the host),
 * only products for that SEO region are loaded. When omitted, keeps legacy behavior (central + Sharon fallback).
 * Must match server-side fetchShowRegionEvents so regional pages are not empty after client filtering.
 */
export const fetchShowCatalogProductsForRegionClient = async (region?: string): Promise<IShowCatalogProduct[]> => {
  const catalog = await fetchShowsCatalogClient()

  if (!catalog) {
    return []
  }

  const allProducts = Object.values(catalog.products)

  if (!region) {
    return filterEventsByCentralRegion(allProducts)
  }

  return filterShowCatalogByRegionSlug(allProducts, region)
}

/**
 * Fetches and filters events for central region (client-side)
 * Excludes promoted products
 */
export const fetchCentralEventsClient = async (): Promise<IShowCatalogProduct[]> => {
  return fetchShowCatalogProductsForRegionClient()
}
